import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { OnboardingCheckIcon } from '../../../theme/icons/OnboardingCheckIcon';
import CustomRealtimeUpdateIcon from '../../../theme/icons/CustomRealtimeUpdateIcon';
import CustomGeofencingIcon from '../../../theme/icons/CustomGeofencingIcon';
import CustomIssuanceFormIcon from '../../../theme/icons/CustomIssuanceFormIcon';
import CustomDownloadPageOutlineIcon from '../../../theme/icons/CustomDownloadPageOutlineIcon';
import CustomReaderSetupIcon from '../../../theme/icons/CustomReaderSetupIcon';
import CustomCsvIcon from '../../../theme/icons/CustomCsvIcon';
import { useListPassTemplatesQuery } from '../../../store/redux/api/templateApi';
import { useBilling } from '../../../store/client/BillingContext';

type ExploreFeatureKey =
  | 'realtime'
  | 'geo'
  | 'issuance'
  | 'download'
  | 'reader'
  | 'bulk';

type ExploreFeature = {
  key: ExploreFeatureKey;
  IconComponent: React.ComponentType<{ boxSize?: string; color?: string }>;
  title: string;
  blurb: string;
};

const EXPLORE_FEATURES: readonly ExploreFeature[] = [
  {
    key: 'realtime',
    IconComponent: CustomRealtimeUpdateIcon,
    title: 'Make a real-time update',
    blurb:
      'Edit the design or a field and watch it change in your wallet instantly.',
  },
  {
    key: 'issuance',
    IconComponent: CustomIssuanceFormIcon,
    title: 'Create a custom issuance form',
    blurb:
      'A customer-facing form that collects details and issues a pass automatically – share via QR or link.',
  },
  {
    key: 'bulk',
    IconComponent: CustomCsvIcon,
    title: 'Issue passes in bulk',
    blurb: 'Upload a CSV to create and send hundreds of passes at once.',
  },
  {
    key: 'download',
    IconComponent: CustomDownloadPageOutlineIcon,
    title: 'Design a download page',
    blurb:
      'Customise the page where customers can add your pass directly to their wallet.',
  },
  {
    key: 'geo',
    IconComponent: CustomGeofencingIcon,
    title: 'Set up geo-based notifications',
    blurb: 'Send automatic notifications when customers are near your venue.',
  },
  {
    key: 'reader',
    IconComponent: CustomReaderSetupIcon,
    title: 'Set up a reader',
    blurb: 'Download the reader app and try scanning your pass.',
  },
];

const hrefFor = (
  key: ExploreFeatureKey,
  firstTemplateId: string | undefined
): string => {
  switch (key) {
    case 'realtime':
      return firstTemplateId
        ? `/pass-templates/edit/${firstTemplateId}`
        : '/pass-templates';
    case 'geo':
      return firstTemplateId
        ? `/pass-templates/edit/${firstTemplateId}?passTab=features&highlight=locations`
        : '/pass-templates';
    case 'issuance':
      return firstTemplateId
        ? `/pass-templates/edit/${firstTemplateId}?tab=form`
        : '/pass-templates';
    case 'download':
      return '/pass-download-pages';
    case 'reader':
      return '/readers/create';
    case 'bulk':
      return '/passes/batches/create';
  }
};

const FeatureRow = ({
  feature,
  href,
  onSelect,
}: {
  feature: ExploreFeature;
  href: string;
  onSelect: () => void;
}) => (
  <Flex
    as={RouterLink}
    to={href}
    position="relative"
    alignItems="center"
    gap={3}
    bg="white"
    border="1px solid"
    borderColor="primaryBackground"
    borderRadius="12px"
    px={4}
    py={3}
    cursor="pointer"
    textDecoration="none"
    transition="border-color 0.15s, transform 0.15s"
    onClick={(e: React.MouseEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      onSelect();
    }}
    role="group"
    sx={{
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        padding: '1.5px',
        borderRadius: 'inherit',
        background: 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)',
        WebkitMask:
          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none',
        opacity: 0,
        transition: 'opacity 0.15s',
      },
      '&:hover::before': { opacity: 1 },
    }}
    _hover={{
      borderColor: 'transparent',
      transform: 'translateY(-1px)',
      textDecoration: 'none',
    }}
  >
    <Flex
      align="center"
      justify="center"
      boxSize="32px"
      flex="0 0 auto"
      color="inkBlue"
      transition="color 0.15s"
      _groupHover={{ color: 'onboardingAccent' }}
    >
      <Icon as={feature.IconComponent} boxSize="24px" />
    </Flex>
    <Box flex="1" minW={0} textAlign="left">
      <Text
        fontFamily="heading"
        fontSize="14px"
        fontWeight="700"
        color="inkBlue"
        mb={0.5}
      >
        {feature.title}
      </Text>
      <Text
        fontFamily="body"
        fontSize="12px"
        lineHeight="1.4"
        color="inkBlue"
        opacity={0.75}
      >
        {feature.blurb}
      </Text>
    </Box>
  </Flex>
);

const OnboardingFinalModal = ({
  isOpen,
  onClose,
  onGoLive,
}: {
  isOpen: boolean;
  onClose: () => void;
  onGoLive: () => void;
}) => {
  const [step, setStep] = useState<'completion' | 'explore'>('completion');

  useEffect(() => {
    if (isOpen) setStep('completion');
  }, [isOpen]);

  const { subscription } = useBilling() as {
    subscription?: { status?: string; onTrial?: boolean } | null;
  };
  const isPaidSubscriber =
    subscription?.status === 'active' && !subscription?.onTrial;

  const { data: templates } = useListPassTemplatesQuery(
    { perPage: 1 },
    { skip: !isOpen || step !== 'explore' }
  );
  const firstTemplateId = templates?.data?.[0]?.id;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="lg"
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="20px"
        overflow="hidden"
        bg="white"
        boxShadow="0 24px 60px -16px rgba(17,22,34,0.55), 0 6px 18px -4px rgba(232,93,58,0.35)"
      >
        <ModalBody p={0}>
          {step === 'completion' ? (
            <>
              {/* ── Orange header section ─────────────────────────────── */}
              <Box
                background="onboardingBgGradient"
                color="white"
                px={8}
                pt={8}
                pb={8}
                textAlign="center"
              >
                <Flex justify="center" mb={4}>
                  <Flex
                    align="center"
                    justify="center"
                    boxSize="40px"
                    borderRadius="full"
                    border="1.5px solid"
                    borderColor="whiteAlpha.800"
                    color="white"
                  >
                    <OnboardingCheckIcon boxSize="18px" />
                  </Flex>
                </Flex>

                <Text
                  fontFamily="heading"
                  fontSize="11px"
                  letterSpacing="2px"
                  textTransform="uppercase"
                  fontWeight="700"
                  color="white"
                  mb={3}
                >
                  Setup complete
                </Text>

                <Text
                  fontFamily="heading"
                  fontSize="24px"
                  fontWeight="800"
                  letterSpacing="-0.3px"
                  lineHeight="1.2"
                  color="white"
                >
                  You've built it and seen it work!
                </Text>
              </Box>

              {/* ── White body section ────────────────────────────────── */}
              <Box
                bg="white"
                color="inkBlue"
                px={10}
                pt={7}
                pb={7}
                textAlign="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="20px"
                  fontWeight="700"
                  lineHeight="1.25"
                  color="inkBlue"
                  mb={3}
                >
                  {isPaidSubscriber
                    ? 'Ready to explore more?'
                    : 'Ready to go live?'}
                </Text>

                <Text
                  fontFamily="body"
                  fontSize="13px"
                  lineHeight="1.5"
                  color="inkBlue"
                  opacity={0.85}
                  mb={6}
                >
                  {isPaidSubscriber
                    ? "You've covered the essentials – template, pass, wallet, notifications. But there's a lot more PassEntry can do – take a look at a few features worth trying next."
                    : "You've covered the essentials – template, pass, wallet, notifications. There's a lot more to explore, but you're ready to start issuing passes to real customers."}
                </Text>

                {isPaidSubscriber ? (
                  <Button
                    onClick={() => setStep('explore')}
                    variant="gradient"
                    w="full"
                  >
                    Explore features
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={onGoLive}
                      variant="gradient"
                      w="full"
                      mb={3}
                    >
                      Select a plan
                    </Button>
                    <Button
                      onClick={() => setStep('explore')}
                      variant="secondary"
                      w="full"
                    >
                      Try more features first
                    </Button>
                  </>
                )}
              </Box>
            </>
          ) : (
            <>
              {/* ── Orange compact header ─────────────────────────────── */}
              <Box
                background="onboardingBgGradient"
                color="white"
                px={6}
                py={6}
                textAlign="center"
                position="relative"
              >
                <IconButton
                  aria-label="Back"
                  icon={<ChevronLeftIcon boxSize="22px" />}
                  onClick={() => setStep('completion')}
                  size="sm"
                  variant="ghost"
                  color="white"
                  position="absolute"
                  left={3}
                  top={3}
                  _hover={{ bg: 'whiteAlpha.200' }}
                />
                <Text
                  fontFamily="heading"
                  fontSize="11px"
                  letterSpacing="2px"
                  textTransform="uppercase"
                  fontWeight="700"
                  color="white"
                  mb={1.5}
                >
                  Features
                </Text>
                <Text
                  fontFamily="heading"
                  fontSize="22px"
                  fontWeight="800"
                  letterSpacing="-0.3px"
                  lineHeight="1.2"
                  color="white"
                  mb={1}
                >
                  More to explore
                </Text>
                <Text
                  fontFamily="body"
                  fontSize="14px"
                  lineHeight="1.4"
                  color="whiteAlpha.900"
                >
                  {isPaidSubscriber
                    ? 'Here are a few features worth trying next.'
                    : 'Here are some things you can try before going live.'}
                </Text>
              </Box>

              {/* ── White feature list ───────────────────────────────── */}
              <Box bg="white" px={5} pt={4} pb={5}>
                <Flex direction="column" gap={2} mb={4}>
                  {EXPLORE_FEATURES.map((feature) => (
                    <FeatureRow
                      key={feature.key}
                      feature={feature}
                      href={hrefFor(feature.key, firstTemplateId)}
                      onSelect={onClose}
                    />
                  ))}
                </Flex>

                <Button onClick={onClose} variant="secondary" w="full">
                  I'll explore on my own
                </Button>
              </Box>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default OnboardingFinalModal;
